import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import DonateButton from "../components/site/DonateButton";
import { IMAGES } from "../lib/images";
import { useReveal } from "../lib/useReveal";
import { HandHeart, Package, Handshake, ArrowRight } from "lucide-react";

const MISSION = [
  {
    icon: HandHeart,
    title: "Relieving Poverty",
    text: "We exist to prevent and relieve poverty, hardship and suffering — responding to real needs with compassion and care.",
  },
  {
    icon: Package,
    title: "Practical Assistance",
    text: "Through humanitarian assistance, grants, essential goods and services, we support individuals, families and communities in tangible ways.",
  },
  {
    icon: Handshake,
    title: "Responsible Partnerships",
    text: "We collaborate with other organisations so that help reaches vulnerable communities effectively, accountably and with dignity.",
  },
];

export default function Home() {
  const ref = useReveal();
  return (
    <div ref={ref}>
      <Seo
        title=""
        path="/"
        description="Mount Moriah Relief is a Northern Ireland charity bringing hope, relieving hardship and restoring dignity through humanitarian assistance — with a particular focus on vulnerable communities in Zimbabwe."
      />

      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center bg-charcoal text-white">
        <img
          src={IMAGES.heroZimbabwe.src}
          alt={IMAGES.heroZimbabwe.alt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-charcoal/70" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-28">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-gold mb-6">
            Mount Moriah Relief
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] max-w-3xl">
            Bringing Hope.<br />Relieving Hardship.<br />Restoring Dignity.
          </h1>
          <p className="mt-8 max-w-xl text-lg sm:text-xl text-white/85 leading-relaxed">
            Supporting individuals, families and communities through practical humanitarian
            assistance, compassion and responsible partnerships.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/donate"
              data-testid="hero-support-button"
              className="bg-gold text-white font-medium px-8 py-3.5 hover:bg-gold-dark transition-colors duration-300"
            >
              Support Our Mission
            </Link>
            <Link
              to="/our-work"
              data-testid="hero-discover-button"
              className="border border-white/80 text-white font-medium px-8 py-3.5 hover:bg-white hover:text-charcoal transition-colors duration-300"
            >
              Discover Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 lg:py-32" aria-labelledby="mission-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-gold mb-4">Our Mission</p>
          <h2 id="mission-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-forest tracking-tight leading-snug max-w-2xl reveal">
            Practical compassion, delivered with care
          </h2>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {MISSION.map((m) => (
              <article key={m.title} className="reveal bg-white border border-sandstone p-8 lg:p-10 hover:-translate-y-1 hover:shadow-md hover:shadow-black/5 transition-[transform,box-shadow] duration-300">
                <m.icon size={28} strokeWidth={1.5} className="text-gold" aria-hidden="true" />
                <h3 className="mt-6 font-serif text-2xl font-semibold text-charcoal">{m.title}</h3>
                <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">{m.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Our story */}
      <section className="py-24 lg:py-32 bg-sandstone/50" aria-labelledby="story-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 reveal">
            <img
              src={IMAGES.volunteersAid.src}
              alt={IMAGES.volunteersAid.alt}
              loading="lazy"
              className="w-full aspect-[4/5] object-cover border border-sandstone"
            />
            <p className="mt-2 text-xs text-charcoal/50 italic">Illustrative photograph</p>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-gold mb-4">Our Story</p>
            <h2 id="story-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-forest tracking-tight leading-snug">
              A name rooted in provision
            </h2>
            <div className="mt-6 space-y-5 text-charcoal/80 leading-relaxed max-w-2xl">
              <p>
                Mount Moriah Relief was established in Northern Ireland in 2024 with a simple
                conviction: that practical compassion, offered responsibly, can change the
                course of a family's life.
              </p>
              <p>
                Our name comes from Mount Moriah — the place associated in the biblical account
                with God's provision, where it is said, "On the mountain of the Lord it will be
                provided." That promise shapes how we work: we exist so that provision reaches
                people facing poverty, hardship and suffering.
              </p>
              <p>
                From our base in Northern Ireland, we have a particular — though not exclusive —
                focus on supporting vulnerable communities in Zimbabwe, working alongside trusted
                partners to deliver humanitarian assistance where it is needed most.
              </p>
            </div>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-forest font-medium hover:gap-3 transition-[gap] duration-300"
              data-testid="story-read-more-link"
            >
              Read more about us <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Where we work */}
      <section className="py-24 lg:py-32" aria-labelledby="where-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-gold mb-4">Where We Work</p>
          <h2 id="where-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium text-forest tracking-tight leading-snug max-w-2xl reveal">
            Two homes, one calling
          </h2>
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            <article className="reveal group">
              <div className="overflow-hidden">
                <img src={IMAGES.northernIreland.src} alt={IMAGES.northernIreland.alt} loading="lazy"
                  className="w-full aspect-[16/10] object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              </div>
              <div className="bg-white border border-t-0 border-sandstone p-8">
                <h3 className="font-serif text-2xl font-semibold text-charcoal">Northern Ireland</h3>
                <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">
                  Our organisational home. Mount Moriah Relief was founded here in 2024, and it is
                  from here that our governance, stewardship and partnerships are directed.
                </p>
              </div>
            </article>
            <article className="reveal group">
              <div className="overflow-hidden">
                <img src={IMAGES.victoriaFalls.src} alt={IMAGES.victoriaFalls.alt} loading="lazy"
                  className="w-full aspect-[16/10] object-cover group-hover:scale-[1.02] transition-transform duration-500" />
              </div>
              <div className="bg-white border border-t-0 border-sandstone p-8">
                <h3 className="font-serif text-2xl font-semibold text-charcoal">Zimbabwe</h3>
                <p className="mt-3 text-sm text-charcoal/75 leading-relaxed">
                  An important area of humanitarian focus. We are developing responsible
                  partnerships to support vulnerable communities — and we will report openly as
                  work progresses.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Impact — honest by design */}
      <section className="py-24 lg:py-32 bg-white border-y border-sandstone" aria-labelledby="impact-heading">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-gold mb-4">Our Impact</p>
          <h2 id="impact-heading" className="reveal font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug">
            Measured honestly, reported openly
          </h2>
          <p className="reveal mt-6 text-charcoal/80 leading-relaxed">
            As a newly established organisation, we will never publish figures we cannot verify.
            As our programmes develop, we will report confirmed outcomes here — and in our annual
            reports — so that every supporter can see exactly what their generosity achieves.
          </p>
          <Link
            to="/our-impact"
            className="reveal mt-8 inline-flex items-center gap-2 text-forest font-medium hover:gap-3 transition-[gap] duration-300"
            data-testid="impact-approach-link"
          >
            How we will measure impact <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* Donation CTA */}
      <section className="py-24 lg:py-32 bg-forest text-white" aria-labelledby="donate-heading">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <h2 id="donate-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-snug">
              Your Compassion Can Make a Difference.
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-xl">
              Every gift supports our charitable purposes — humanitarian assistance, grants,
              essential goods and services for people facing hardship. Donations are processed
              securely through our official JustGiving page; we never handle your card details.
            </p>
            <div className="mt-8">
              <DonateButton testId="home-donate-button">Donate via JustGiving</DonateButton>
            </div>
          </div>
          <div className="lg:col-span-5 reveal">
            <img src={IMAGES.harvest.src} alt={IMAGES.harvest.alt} loading="lazy"
              className="w-full aspect-[4/3] object-cover border border-white/20" />
            <p className="mt-2 text-xs text-white/50 italic">Illustrative photograph</p>
          </div>
        </div>
      </section>
    </div>
  );
}
